import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authService } from '../services/AuthenticationService';

export interface RhAuthUser {
  email?: string;
  nickname?: string;
  name?: string;
  picture?: string;
  email_verified?: boolean;
  sub?: string;
  roles?: string[];
  [key: string]: any;
}

interface RhAuthContextValue {
  user: RhAuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  getAccessTokenSilently: () => Promise<string | null>;
  getIdTokenClaims: () => RhAuthUser | null;
  logout: (options?: { returnTo?: string }) => void;
  refreshUser: () => void;
}

const RhAuthContext = createContext<RhAuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  getAccessTokenSilently: async () => null,
  getIdTokenClaims: () => null,
  logout: () => {},
  refreshUser: () => {},
});

export const RhAuthProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<RhAuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(() => {
    const isAuth = authService.isAuthenticated();
    if (isAuth) {
      const userInfo = authService.getUserInfo();
      setUser(userInfo);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    refreshUser();
    window.addEventListener('rh-auth-change', refreshUser);
    return () => window.removeEventListener('rh-auth-change', refreshUser);
  }, [refreshUser]);

  const logout = useCallback((options?: { returnTo?: string }) => {
    authService.logout();
    window.location.href = options?.returnTo || '/login';
  }, []);

  const getAccessTokenSilently = useCallback(async (): Promise<string | null> => {
    if (authService.isAuthenticated()) {
      return authService.getAccessToken();
    }
    const refreshed = await authService.silentRefresh();
    return refreshed ? authService.getAccessToken() : null;
  }, []);

  const getIdTokenClaims = useCallback((): RhAuthUser | null => {
    return authService.getUserInfo();
  }, []);

  return (
    <RhAuthContext.Provider
      value={{ user, isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims, logout, refreshUser }}>
      {children}
    </RhAuthContext.Provider>
  );
};

export const useRhAuth = () => useContext(RhAuthContext);
